/**
 * Client for the `tool` edge function. No component talks to the `user_tools`
 * table directly. Field names are the column names.
 *
 * Custom and native tools are added and edited through their own routes
 * (`/tool/custom`, `/tool/native`), each taking only its own columns; the list,
 * a read and a delete serve both. The function also keeps the agent's
 * `config.toolIds` / `config.nativeTools` in step on add, native edit and
 * delete, so no caller writes the agent after a tool call. Keep in sync with
 * `supabase/functions/tool/{custom,native}/validation.ts`.
 */

import { api } from "@/lib/api-client";
import { type ListParams, MAX_PAGE_SIZE } from "@/lib/list-query";
import type { ResultScheduling, ToolBehavior } from "@/utils/tool-options";

const BASE = "/tool";

/** What the tools panel shows before it pages. Its own size, not the app-wide
 * default of 10: the panel sits inside the agent editor, where a short list
 * that pages every few tools reads worse than one screen of them. */
export const TOOLS_PAGE_SIZE = 20;

/** bot.py reads this off the `end_call` row — the server's `END_CALL_DESCRIPTION`, verbatim. */
export const END_CALL_DESCRIPTION =
  "End the call when user has to leave (like says bye) or you are instructed to do so.";

/** The tools bot.py implements itself. */
export const NATIVE_TOOL_NAMES = ["end_call", "send_sms", "transfer_call"] as const;
export type NativeToolName = (typeof NATIVE_TOOL_NAMES)[number];

/**
 * What the function answers in place of a saved secret: each custom tool's
 * header values, and a send_sms tool's Twilio Account SID. Sent back on an edit,
 * it keeps the saved value; a new tool cannot send it. shared/tool-options.json's
 * `saved_secret`, which tool.service.test.ts pins.
 */
export const SAVED_SECRET = "********";

/** A `user_tools` row minus what the function withholds — the Twilio auth token
 * is write-only, so a stored one is never read back — with its other secrets
 * answered as `SAVED_SECRET`: every header value, and the Account SID when one
 * is saved. */
export interface AgentTool {
  id: string;
  agent_id: string;
  tool_name: string;
  description: string | null;
  endpoint_url: string;
  http_method: string;
  headers: Record<string, string> | null;
  parameters: { properties?: Record<string, unknown>; required?: string[] } | null;
  timeout_seconds: number | null;
  is_native: boolean | null;
  sms_content_type: "prompt" | "static" | null;
  sms_static_message: string | null;
  transfer_call_mode: "static" | "dynamic" | null;
  transfer_call_destination: string | null;
  transfer_call_type: "cold" | "warm" | null;
  twilio_account_sid: string | null;
  twilio_from_number: string | null;
  /** Custom tools only (`utils/tool-options.ts`); natives never carry them. */
  tool_behavior: ToolBehavior | null;
  result_scheduling: ResultScheduling | null;
  created_at: string;
  updated_at: string;
}

/** The server's rule, which is bot.py's: a native name on a row not marked
 * native is a custom tool. */
export const isNativeTool = (
  tool: Pick<AgentTool, "is_native" | "tool_name">,
): tool is Pick<AgentTool, "is_native"> & { tool_name: NativeToolName } =>
  tool.is_native === true && (NATIVE_TOOL_NAMES as readonly string[]).includes(tool.tool_name);

/** A custom tool: an HTTP endpoint bot.py calls with the arguments the model chose.
 * On an update, a `null` option is written — it clears to the platform default. */
type CustomColumns = Pick<
  AgentTool,
  | "tool_name"
  | "description"
  | "endpoint_url"
  | "http_method"
  | "headers"
  | "parameters"
  | "timeout_seconds"
  | "tool_behavior"
  | "result_scheduling"
>;

export type CreateCustomToolBody = Partial<CustomColumns> &
  Pick<CustomColumns, "tool_name" | "endpoint_url" | "http_method"> & { agent_id: string };

export type UpdateCustomToolBody = Partial<CustomColumns>;

/** A native tool's settings. Its endpoint, method, headers and parameters are
 * the server's, and each native takes only its own. */
type NativeColumns = Pick<
  AgentTool,
  | "description"
  | "timeout_seconds"
  | "sms_content_type"
  | "sms_static_message"
  | "twilio_account_sid"
  | "twilio_from_number"
  | "transfer_call_mode"
  | "transfer_call_destination"
  | "transfer_call_type"
> & {
  /** Write-only. Leave it out of an update to keep the stored token. */
  twilio_auth_token: string | null;
};

export type CreateNativeToolBody = Partial<NativeColumns> & {
  agent_id: string;
  tool_name: NativeToolName;
};

export type UpdateNativeToolBody = Partial<NativeColumns>;

/** One page of the agent's tools, both kinds, newest first. */
export const listTools = (agentId: string, params: ListParams) =>
  api.postPage<AgentTool>(`${BASE}/list`, params, { agent_id: agentId });

/**
 * The agent's built-ins, whole. The add-tool dialog greys out a card the agent
 * already has, which a page of the full list cannot answer once there are more
 * tools than fit on one — so it asks for this instead. An agent has at most a
 * handful of native rows, so one request at the ceiling is all of them.
 */
export const listNativeTools = async (agentId: string) =>
  (await api.postPage<AgentTool>(
    `${BASE}/list`,
    { page: 1, size: MAX_PAGE_SIZE, search: "" },
    { agent_id: agentId, is_native: true },
  )).rows;

export const getTool = (id: string) => api.getItem<AgentTool>(`${BASE}/${id}`);

export const deleteTool = (id: string) => api.del(`${BASE}/${id}`);

/** Also adds the tool to its agent's `config.toolIds`, server-side. */
export const createCustomTool = (body: CreateCustomToolBody) =>
  api.post<AgentTool>(`${BASE}/custom`, body);

export const updateCustomTool = (id: string, body: UpdateCustomToolBody) =>
  api.put<AgentTool>(`${BASE}/custom/${id}`, body);

/** Also adds the tool to its agent's `config.toolIds` and switches it on in
 * `config.nativeTools`, server-side. */
export const createNativeTool = (body: CreateNativeToolBody) =>
  api.post<AgentTool>(`${BASE}/native`, body);

/** Also mirrors the updated tool into its agent's `config.nativeTools`. */
export const updateNativeTool = (id: string, body: UpdateNativeToolBody) =>
  api.put<AgentTool>(`${BASE}/native/${id}`, body);
